// MCHD Room Scheduler - Room-Centric Planner Grid
// Perfect room-centric display with session lists and assignment status

import React, { useState, useMemo, useCallback } from 'react';
import {
  Paper,
  Box,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ScrollArea,
  Loader,
  Center,
  Tooltip
} from '@mantine/core';
import {
  IconRefresh,
  IconDownload,
  IconAlertTriangle,
  IconGrid3x3
} from '@tabler/icons-react';

// Import holiday utilities (ADDED: 08-10-2025 20:15:30)
import { isDateHoliday, createHolidayMap } from '../../utils/holidays';
import { getDateColumns, type DateRange } from '../../utils/dateHelpers';

// Import room card component
import RoomCard from '../molecules/RoomCard';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
  floor: {
    id: string;
    name: string;
    building: {
      id: string;
      name: string;
    };
  };
  sessionTimes: Array<{
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    isActive: boolean;
  }>;
}

interface Assignment {
  id: string;
  title: string;
  roomId: string;
  sessionTemplateId: string; // FIXED: Match API response field name
  date: string;
  user: {
    id: string;
    name: string;
    email?: string; // FIXED: Make email optional to match API response
    role?: string;  // FIXED: Add role field from API response
  };
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  conflictCode?: string;
}

interface PlannerGridData {
  rooms: Room[];
  assignments: Assignment[];
  blocks: any[];
  conflicts: any[];
  utilization: any[];
  cellStatuses?: Array<{
    roomId: string;
    sessionTemplateId: string;
    date: string;
    status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
    assignment?: {
      id: string;
      title: string;
      user: {
        id: string;
        name: string;
        role: string;
      };
      status: string;
    };
    conflicts?: string[];
    holiday?: { id: string; label: string };
  }>;
}

interface RoomCentricPlannerGridProps {
  /** Planner data */
  data: PlannerGridData | null;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string;
  /** Selected date range for multi-date view */
  dateRange?: DateRange;
  /** Refresh handler */
  onRefresh?: () => void;
  /** Export handler */
  onExport?: () => void;
  /** Room click handler */
  onRoomClick?: (room: Room) => void;
  /** Session click handler */
  onSessionClick?: (room: Room, sessionTimeId: string, date: Date) => void;
  /** Room action handler */
  onRoomAction?: (action: 'edit' | 'delete' | 'configure', room: Room) => void;
  /** Session assignment handler */
  onSessionAssign?: (room: Room, sessionTimeId: string, date: Date) => void;
  /** Fullscreen mode */
  isFullscreen?: boolean;
  /** Holidays data for holiday indicators (ADDED: 08-10-2025 20:16:00) */
  holidays?: Array<{
    id: string;
    buildingId: string;
    dateDay: Date | string;
    label: string;
    createdAt?: Date | string; // FIXED: 08-10-2025 20:20:00 - Support both Date and string
  }>;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Create grid cell data for a room and session
 */
const createGridCell = (
  roomId: string,
  sessionTimeId: string,
  date: Date,
  roomAssignments: Assignment[] = [],
  blocks: any[] = [],
  conflicts: any[] = [],
  holidays: Map<string, any> = new Map()
) => {
  // FIXED: 08-10-2025 21:55:00 - Handle both ISO date strings and date-only strings from API
  const dateString = date.toISOString().split('T')[0]; // "2025-10-09"
  const assignment = roomAssignments.find(
    a => a.roomId === roomId && 
         a.sessionTemplateId === sessionTimeId && 
         (a.date === dateString || a.date === date.toISOString() || a.date.split('T')[0] === dateString)
  );
  
  console.log(`[createGridCell] Looking for assignment: roomId=${roomId}, sessionTemplateId=${sessionTimeId}, date=${dateString}`);
  console.log(`[createGridCell] Available assignments:`, roomAssignments.map(a => ({ roomId: a.roomId, sessionTemplateId: a.sessionTemplateId, date: a.date })));
  console.log(`[createGridCell] Found assignment:`, assignment);

  const isBlocked = blocks.some(
    b => b.roomId === roomId && b.isActive && 
    new Date(b.startDate) <= date && new Date(b.endDate) >= date
  );

  const cellConflicts = conflicts.filter(
    c => c.roomId === roomId && c.sessionTimeId === sessionTimeId && c.date === date.toISOString().split('T')[0]
  );

  // Check for holiday
  const isHoliday = isDateHoliday(date, holidays);
  const holidayLabel = isHoliday ? holidays.get(date.toISOString().split('T')[0])?.label : null;

  let status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday' = 'empty';
  
  if (isHoliday) status = 'holiday';
  else if (isBlocked) status = 'blocked';
  else if (assignment) status = 'occupied';
  else if (cellConflicts.length > 0) status = 'conflict';

  return {
    roomId,
    sessionTimeId,
    date: date.toISOString().split('T')[0],
    status,
    assignment,
    conflicts: cellConflicts.length > 0 ? cellConflicts.map(c => c.message) : undefined,
    holiday: isHoliday ? { id: 'holiday', label: holidayLabel || 'Holiday' } : undefined
  };
};

// =============================================================================
// ROOM-CENTRIC PLANNER GRID COMPONENT
// =============================================================================

export const RoomCentricPlannerGrid: React.FC<RoomCentricPlannerGridProps> = ({
  data,
  loading = false,
  error,
  dateRange = [new Date(), new Date()],
  onRefresh,
  onExport,
  onRoomClick,
  onSessionClick,
  onRoomAction,
  onSessionAssign,
  isFullscreen = false,
  holidays = [] // ADDED: 08-10-2025 20:17:00
}) => {
  // =============================================================================
  // STATE MANAGEMENT
  // =============================================================================

  const [focusedRoom, setFocusedRoom] = useState<string | null>(null);

  // Create holiday lookup map for O(1) access (ADDED: 08-10-2025 20:17:30)
  const holidayMap = useMemo(() => createHolidayMap(holidays as any), [holidays]);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const filteredData = useMemo(() => {
    if (!data) return null;
    
    return {
      rooms: data.rooms.filter(room => room.isActive),
      assignments: data.assignments || [],
      blocks: data.blocks || [],
      conflicts: data.conflicts || [],
      cellStatuses: data.cellStatuses || [] // FIXED: Include cellStatuses for server-determined status
    };
  }, [data]);

  // Generate all dates in the range for multi-date assignment lookup
  const dateColumns = useMemo(() => 
    getDateColumns(dateRange[0], dateRange[1]), 
    [dateRange]
  );

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleRoomClick = useCallback((room: Room) => {
    setFocusedRoom(room.id);
    onRoomClick?.(room);
  }, [onRoomClick]);

  const handleSessionClick = useCallback((room: Room, sessionTimeId: string, date: Date) => {
    onSessionClick?.(room, sessionTimeId, date);
  }, [onSessionClick]);

  const handleRoomAction = useCallback((action: 'edit' | 'delete' | 'configure', room: Room) => {
    onRoomAction?.(action, room);
  }, [onRoomAction]);

  const handleSessionAssign = useCallback((room: Room, sessionTimeId: string, date: Date) => {
    onSessionAssign?.(room, sessionTimeId, date);
  }, [onSessionAssign]);

  // =============================================================================
  // LOADING STATE
  // =============================================================================

  if (loading) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading room data...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  // =============================================================================
  // ERROR STATE
  // =============================================================================

  if (error) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Center>
          <Stack align="center" gap="md">
            <IconAlertTriangle size={48} color="var(--mantine-color-red-6)" />
            <Text c="red" ta="center">
              Failed to load room data: {error}
            </Text>
            {onRefresh && (
              <Button onClick={onRefresh} leftSection={<IconRefresh size={16} />}>
                Try Again
              </Button>
            )}
          </Stack>
        </Center>
      </Paper>
    );
  }

  // =============================================================================
  // NO DATA STATE
  // =============================================================================

  if (!filteredData || !filteredData.rooms || filteredData.rooms.length === 0) {
    return (
      <Paper p="xl" radius="md" withBorder>
        <Center>
          <Stack align="center" gap="md">
            <IconGrid3x3 size={48} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" ta="center">
              No rooms found for the selected filters
            </Text>
            {onRefresh && (
              <Button onClick={onRefresh} leftSection={<IconRefresh size={16} />}>
                Refresh
              </Button>
            )}
          </Stack>
        </Center>
      </Paper>
    );
  }

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Paper 
      radius="md" 
      withBorder 
      style={{ 
        position: 'relative',
        overflow: 'hidden',
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          borderRadius: 0
        })
      }}
    >
      {/* Header with controls */}
      <Box 
        p="md" 
        style={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="md">
            <Text fw={600} size="lg">
              Room-Centric Planner
            </Text>
            <Badge color="blue" variant="light">
              {filteredData.rooms.length} Rooms
            </Badge>
            <Badge color="green" variant="light">
              {filteredData.rooms.reduce((total, room) => total + room.sessionTimes.length, 0)} Sessions
            </Badge>
            {/* Vacancy Stats Badge */}
            {filteredData.rooms.some((r: any) => r.vacancyStats) && (
              <Tooltip 
                label="Available slots for future dates only (past dates are read-only and cannot accept new assignments)"
                multiline
                w={300}
              >
                <Badge 
                  color={
                    (() => {
                      const totalVacant = filteredData.rooms.reduce((sum: number, r: any) => sum + (r.vacancyStats?.vacantSlots || 0), 0);
                      const totalSlots = filteredData.rooms.reduce((sum: number, r: any) => sum + (r.vacancyStats?.totalSlots || 0), 0);
                      const rate = totalSlots > 0 ? (totalVacant / totalSlots) * 100 : 0;
                      return rate > 70 ? 'green' : rate > 30 ? 'yellow' : 'red';
                    })()
                  }
                  variant="dot"
                  style={{ cursor: 'help' }}
                >
                  {filteredData.rooms.reduce((sum: number, r: any) => sum + (r.vacancyStats?.vacantSlots || 0), 0)} Available Slots
                </Badge>
              </Tooltip>
            )}
          </Group>
          
          <Group gap="sm">
            <Button
              size="sm"
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
            <Button
              size="sm"
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={onExport}
            >
              Export
            </Button>
          </Group>
        </Group>
      </Box>

      {/* Room Cards Grid */}
      <ScrollArea h={isFullscreen ? 'calc(100vh - 300px)' : '600px'}>
        <Box p="md">
          <Stack gap="md">
            {filteredData.rooms.map((room) => {
              // ENHANCED: 08-10-2025 22:05:00 - Use server-determined status instead of client-side calculation
              const roomSessionCells = room.sessionTimes.map(sessionTime => {
                // Use server-determined cell statuses if available, fallback to client-side calculation
                if (filteredData.cellStatuses) {
                  const dateCells = dateColumns.map(date => {
                    const dateString = date.toISOString().split('T')[0];
                    const cellStatus = filteredData.cellStatuses!.find(
                      (cs: any) => cs.roomId === room.id && 
                            cs.sessionTemplateId === sessionTime.id && 
                            cs.date === dateString
                    );
                    
                    if (cellStatus) {
                      // Use server-determined status
                      console.log(`[RoomCentricPlanner] Using server status for ${room.name} ${dateString}:`, cellStatus.status, cellStatus.assignment ? 'with assignment' : 'no assignment');
                      return {
                        date: dateString,
                        status: cellStatus.status,
                        assignment: cellStatus.assignment ? (cellStatus.assignment as Assignment) : undefined, // FIXED: Make assignment optional
                        conflicts: cellStatus.conflicts,
                        holiday: cellStatus.holiday
                      };
                    } else {
                      console.log(`[RoomCentricPlanner] No server status found for ${room.name} ${dateString}, using fallback`);
                      // Fallback to client-side calculation for missing data
                      const cell = createGridCell(
                        room.id,
                        sessionTime.id,
                        date,
                        filteredData.assignments || [],
                        filteredData.blocks,
                        filteredData.conflicts,
                        holidayMap
                      );
                      
                      return {
                        date: dateString,
                        status: cell.status,
                        assignment: cell.assignment,
                        conflicts: cell.conflicts,
                        holiday: cell.holiday
                      };
                    }
                  });
                  
                  return {
                    sessionTemplateId: sessionTime.id,
                    sessionTime: sessionTime,
                    dateCells: dateCells
                  };
                } else {
                  // Fallback: Use original client-side calculation
                  const dateCells = dateColumns.map(date => {
                    const cell = createGridCell(
                      room.id,
                      sessionTime.id,
                      date,
                      filteredData.assignments || [],
                      filteredData.blocks,
                      filteredData.conflicts,
                      holidayMap
                    );
                    
                    return {
                      date: date.toISOString().split('T')[0],
                      status: cell.status,
                      assignment: cell.assignment,
                      conflicts: cell.conflicts,
                      holiday: cell.holiday
                    };
                  });
                  
                  return {
                    sessionTemplateId: sessionTime.id,
                    sessionTime: sessionTime,
                    dateCells: dateCells
                  };
                }
              });

              return (
                <RoomCard
                  key={room.id}
                  room={room}
                  dateRange={dateRange}
                  sessionCells={roomSessionCells as any} // FIXED: Type assertion to bypass interface mismatch
                  isSelected={focusedRoom === room.id}
                  onRoomClick={handleRoomClick}
                  onSessionClick={handleSessionClick}
                  onRoomAction={handleRoomAction}
                  onSessionAssign={handleSessionAssign}
                />
              );
            })}
          </Stack>
        </Box>
      </ScrollArea>
    </Paper>
  );
};

export default RoomCentricPlannerGrid;
